import React, { useState } from 'react';
import { CATEGORIES } from '../../constants/categories';
import { SERVICE_CATEGORIES, SERVICE_SUBCATEGORIES } from '../../constants/eventTypes';
import { isValidZip, isKnownZip, distanceMiles } from '../../utils/distance';
import VendorCard from '../vendor/VendorCard';

// ─── Matches Page ─────────────────────────────────────────────────────────────
function MatchesPage({ vendors=[], openMessage, sendBookingRequest, bookingRequests, setBookingRequests, hostEvent, setHostEvent, userEvents, setTab, vendorCalendars, setVendorCalendars, authUser, setShowAuthModal, setInquiryModal, setEventMessageModal }) {
  const [filterCategory, setFilterCategory] = useState('');
  const [filterInsurance, setFilterInsurance] = useState('');
  const [filterVendorType, setFilterVendorType] = useState('');
  const [filterService, setFilterService] = useState('');
  const [hostZip, setHostZip] = useState(hostEvent?.eventZip || '');
  const approvedEvents = (userEvents||[]).filter(e => e.status === 'approved' || e.status === 'concierge_active');
  const switchEvent = (e) => {
    const svcNeeded = (() => { try { return typeof e.services_needed === 'string' ? JSON.parse(e.services_needed) : (e.services_needed || []); } catch { return []; } })();
    setHostEvent({ eventName:e.event_name, eventType:e.event_type, eventZip:e.zip, date:e.date, startTime:e.start_time, endTime:e.end_time, contactName:e.contact_name, email:e.contact_email, vendorCategories:e.categories_needed||[], servicesNeeded:svcNeeded, vendorCount:e.spots, budget:e.booth_fee, notes:e.notes, eventId:e.id, source:e.source });
    setHostZip(e.zip || '');
  };
  const [contacted, setContacted] = useState([]);
  const hasZip = hostZip.length === 5 && isValidZip(hostZip);

  const enriched = vendors
    .filter(v => {
      if (!filterVendorType) return true;
      if (filterVendorType === 'market') return !v.isServiceProvider || v.vendorType?.market;
      if (filterVendorType === 'service') return v.isServiceProvider || v.vendorType?.service;
      return true;
    })
    .filter(v => {
      if (!filterCategory) return true;
      const allCats = [...(v.allCategories || [v.category]), ...(v.serviceCategories || [])];
      return allCats.includes(filterCategory);
    })
    .filter(v => !filterService || (v.serviceSubcategories||[]).includes(filterService) || v.serviceType === filterService)
    .filter(v => !filterInsurance || (filterInsurance==='yes' ? v.insurance : !v.insurance))
    .map(v => {
      const dist    = hasZip ? distanceMiles(v.homeZip, hostZip) : null;
      const inRange = !hasZip ? true : (dist === null ? true : dist <= v.radius);
      return { ...v, dist, inRange };
    });

  const inRange  = enriched.filter(v => v.inRange).sort((a,b)=>(a.dist??999)-(b.dist??999)||b.matchScore-a.matchScore);
  const outRange = enriched.filter(v => !v.inRange);

  // Calculate match percentage — how well does this vendor fit this event?
  // A vendor is 100% match if ALL of their categories are needed by the event.
  // A vendor is a partial match if some of their categories are needed.
  // neededCats includes both market vendor categories AND service categories from servicesNeeded
  const neededMarketCats = hostEvent?.vendorCategories || [];
  const neededServiceCats = (hostEvent?.servicesNeeded || []).map(s => s.type).filter(Boolean);
  const neededCats = [...neededMarketCats, ...neededServiceCats];
  const calcMatch = (v) => {
    if (neededCats.length === 0) return null;
    const vendorCats = [...(v.allCategories || [v.category]).filter(Boolean), ...(v.serviceCategories || [])];
    if (vendorCats.length === 0) return 0;
    const matched = vendorCats.filter(c => neededCats.includes(c)).length;
    return Math.round((matched / vendorCats.length) * 100);
  };

  return (
    <div className="section" style={{ maxWidth:1060 }}>
      <div className="section-title">{hostEvent ? `Vendors for ${hostEvent.eventName || 'Your Event'}` : 'Vendor Directory'}</div>
      <p className="section-sub">{hostEvent ? `Find and invite vendors to ${hostEvent.eventName || 'your event'} on ${hostEvent.date || ''}` : 'Browse all active South Jersey vendors. Enter your event zip code to see who can travel to you.'}</p>
      {/* Event picker — switch between approved events */}
      {approvedEvents.length > 0 && (
        <div style={{background:'#1a1410',borderRadius:10,padding:'12px 18px',marginBottom:16}}>
          <div style={{display:'flex',alignItems:'center',justifyContent:'space-between',flexWrap:'wrap',gap:8}}>
            <div style={{display:'flex',alignItems:'center',gap:10}}>
              <label style={{fontSize:11,color:'#a89a8a',fontWeight:600,whiteSpace:'nowrap'}}>Finding vendors for:</label>
              {approvedEvents.length === 1 ? (
                <div style={{fontSize:13,color:'#e8c97a',fontWeight:700}}>{approvedEvents[0].event_name}</div>
              ) : (
                <select value={hostEvent?.eventId || ''} onChange={e=>{const ev=approvedEvents.find(x=>x.id===e.target.value); if(ev)switchEvent(ev);}}
                  style={{background:'#2d2118',color:'#e8c97a',border:'1px solid #c8a84b',borderRadius:6,padding:'4px 10px',fontSize:13,fontWeight:700,fontFamily:'DM Sans,sans-serif',cursor:'pointer'}}>
                  {!hostEvent?.eventId && <option value="">Select an event...</option>}
                  {approvedEvents.map(ev=><option key={ev.id} value={ev.id}>{ev.event_name} — {ev.date}</option>)}
                </select>
              )}
            </div>
            {hostEvent && (
              <div style={{fontSize:11,color:'#a89a8a'}}>{hostEvent.date} · {hostEvent.eventType} · Zip {hostEvent.eventZip}</div>
            )}
          </div>
          {neededCats.length > 0 && <div style={{fontSize:10,color:'#a89a8a',marginTop:6}}>Looking for: {neededCats.join(', ')}</div>}
        </div>
      )}
      <div className="match-filters">
        <div className="match-filter-group" style={{ maxWidth:200 }}>
          <label>{hostEvent ? 'Event Zip Code' : 'My Zip Code'}</label>
          <input placeholder="e.g. 08033" value={hostZip} maxLength={5} onChange={e=>setHostZip(e.target.value.replace(/\D/g,'').slice(0,5))} />
          {hasZip && <div className={`zip-feedback ${isKnownZip(hostZip)?'zip-ok':'zip-warn'}`}>{isKnownZip(hostZip)?'✓ Showing vendors in range':'⚠ Zip unverified — results may vary'}</div>}
          {!hostEvent && !hasZip && <div style={{fontSize:11,color:'#a89a8a',marginTop:2}}>Enter zip to filter by distance</div>}
        </div>
        <div className="match-filter-group">
          <label>Vendor Type</label>
          <select value={filterVendorType} onChange={e=>{setFilterVendorType(e.target.value);if(e.target.value==='market'){setFilterService('');}if(e.target.value==='service'){setFilterCategory('');}}}>
            <option value="">All Vendors</option>
            <option value="market">Market Vendors</option>
            <option value="service">Service Providers</option>
          </select>
        </div>
        {filterVendorType !== 'service' && (
          <div className="match-filter-group">
            <label>Product Category</label>
            <select value={filterCategory} onChange={e=>setFilterCategory(e.target.value)}>
              <option value="">All Categories</option>
              {CATEGORIES.map(c=><option key={c}>{c}</option>)}
            </select>
          </div>
        )}
        {filterVendorType !== 'market' && (
          <div className="match-filter-group">
            <label>Service Type</label>
            <select value={filterService} onChange={e=>setFilterService(e.target.value)}>
              <option value="">All Services</option>
              {SERVICE_CATEGORIES.flatMap(cat=>(SERVICE_SUBCATEGORIES[cat]||[]).filter(s=>s!=='Other')).map(s=><option key={s}>{s}</option>)}
            </select>
          </div>
        )}
        <div className="match-filter-group">
          <label>Insurance</label>
          <select value={filterInsurance} onChange={e=>setFilterInsurance(e.target.value)}>
            <option value="">Any</option><option value="yes">Insured Only</option><option value="no">Not Required</option>
          </select>
        </div>
      </div>

      <div style={{background:'#fdf9f5',border:'1px solid #e8ddd0',borderRadius:8,padding:'10px 14px',marginBottom:16,fontSize:12,color:'#7a6a5a',lineHeight:1.5}}>
        Vendor profiles are self-reported. Before booking, verify insurance, licenses, and any information important to your event. All agreements and payments are between you and the vendor.
      </div>
      <div className="results-header">
        <div className="results-count"><strong>{inRange.length}</strong> {hasZip?'vendors within travel range':'vendors found'}</div>
        {hasZip && <div style={{ fontSize:13, color:'#7a6a5a' }}>Sorted nearest → farthest from {hostZip}</div>}
      </div>

      {inRange.length===0
        ? <div className="empty-state"><div className="big">🔍</div><p>No vendors match your filters.</p></div>
        : <div className="vendor-grid">{inRange.map(v=><VendorCard key={v.id} v={v} contacted={contacted} setContacted={setContacted} showDist={hasZip} openMessage={openMessage} sendBookingRequest={sendBookingRequest} bookingRequests={bookingRequests} setBookingRequests={setBookingRequests} hostEvent={hostEvent} setTab={setTab} vendorCalendars={vendorCalendars} setVendorCalendars={setVendorCalendars} authUser={authUser} setShowAuthModal={setShowAuthModal} matchPct={calcMatch(v)} setInquiryModal={setInquiryModal} setEventMessageModal={setEventMessageModal} userEvents={userEvents} />)}</div>
      }

      {hasZip && outRange.length>0 && (
        <>
          <div style={{ marginTop:48, marginBottom:16, borderTop:'2px dashed #e0d5c5', paddingTop:32 }}>
            <div style={{ fontFamily:"Playfair Display,serif", fontSize:20, marginBottom:4 }}>Outside Travel Range</div>
            <p style={{ fontSize:14, color:'#a89a8a' }}>These vendors are beyond their stated travel radius for zip {hostZip}.</p>
          </div>
          <div className="vendor-grid" style={{ opacity:0.5 }}>
            {outRange.map(v=><VendorCard key={v.id} v={v} contacted={contacted} setContacted={setContacted} showDist outOfRange openMessage={openMessage} sendBookingRequest={sendBookingRequest} bookingRequests={bookingRequests} setBookingRequests={setBookingRequests} hostEvent={hostEvent} setTab={setTab} vendorCalendars={vendorCalendars} setVendorCalendars={setVendorCalendars} authUser={authUser} setShowAuthModal={setShowAuthModal} userEvents={userEvents} />)}
          </div>
        </>
      )}
    </div>
  );
}

export default MatchesPage;
